import { Component, inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogContent,
} from '@angular/material/dialog';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  FormBuilder,
} from '@angular/forms';
import { UserService } from '../../../../../_services/users.service';
import { AlertService } from '../../../../../_services/alert.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { LoquateFinderComponent } from "../../../../../../Shared/_components/loquate-finder/loquate-finder.component";
import { TrimInputDirective } from '../../../../../../Shared/_directives/trim-input.directive';

@Component({
    selector: 'app-create-address-modal',
    imports: [MatDialogContent, ReactiveFormsModule, MatButtonModule, MatIconModule, MatTooltipModule, NgClass, LoquateFinderComponent, TrimInputDirective],
    templateUrl: './create-address-modal.component.html',
    styleUrl: './create-address-modal.component.css'
})
export class CreateAddressModalComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<CreateAddressModalComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);

  createAddressForm: FormGroup = new FormGroup({
    addressType: new FormControl(''),
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    phoneNo: new FormControl(''),
    streetAddressLineOne: new FormControl(''),
    streetAddressLineTwo: new FormControl(''),
    town: new FormControl(''),
    country: new FormControl(''),
    postCode: new FormControl(''),
    email: new FormControl(''),
  });

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.createAddressForm = this.formBuilder.group({
      addressType: new FormControl('BILLING', []),
      firstName: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
      ]),
      lastName: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
      ]),
      phoneNo: new FormControl('', [
        Validators.required,
        Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$'),
      ]),
      isDefault: new FormControl(true),
      streetAddressLineOne: new FormControl('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(200),
      ]),
      streetAddressLineTwo: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(200),
      ]),
      town: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(200),
      ]),
      country: new FormControl('', [
        Validators.minLength(2),
        Validators.maxLength(200),
      ]),
      postCode: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(20),
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.email,
      ]),
    });
    if (this.data.isEdit) {
      this.createAddressForm.setValue({
        firstName: this.data.firstName,
        lastName: this.data.lastName,
        phoneNo: this.data.phoneNo,
        streetAddressLineOne: this.data.address1,
        streetAddressLineTwo: this.data.address2,
        town: this.data.city,
        country: this.data.country,
        postCode: this.data.postCode,
        isDefault: this.data.isDefault,
        addressType: this.data.addressType,
        email: this.data.email.toLowerCase(),
      });
    }
  }

  // Field looks: neutral, focused, or red once it has been touched and is wrong.
  inputCls(name: string): string {
    const c = this.createAddressForm.get(name);
    const bad = !!c && c.invalid && (c.dirty || c.touched);
    return (
      'w-full px-3.5 py-2.5 rounded-xl border text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 ' +
      (bad
        ? 'border-red-300 bg-red-50/40 focus:border-red-400 focus:ring-4 focus:ring-red-100'
        : 'border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100')
    );
  }

  // How many required things are still wrong, for the footer hint.
  get problemCount(): number {
    return Object.values(this.createAddressForm.controls).filter((c) => c.invalid).length;
  }

  get f(): { [key: string]: AbstractControl } {
    return this.createAddressForm.controls;
  }

  onAddressSelected(data: any) {
    const addressData = data.addressData; 
    const type = data.type;
    addressData['firstName'] = this.createAddressForm.get('firstName')?.value;
    addressData['lastName'] = this.createAddressForm.get('lastName')?.value;
    addressData['phoneNo'] = this.createAddressForm.get('phoneNo')?.value;
    addressData['email'] = this.createAddressForm.get('email')?.value;
    addressData['streetAddressLineOne'] = addressData['address1'];
    addressData['streetAddressLineTwo'] = addressData['address2'];
    addressData['town'] = addressData['city'];
    addressData['postCode'] = addressData['postCode'];
    addressData['country'] = addressData['country'];
    addressData['isDefault'] = this.createAddressForm.get('isDefault')?.value;
    addressData['addressType'] = this.createAddressForm.get('addressType')?.value;
    delete addressData['address1'];
    delete addressData['address2'];
    delete addressData['city'];
    this.createAddressForm.setValue(addressData);
  }

  onAddressCreated(event: { newAddressList: any[] }): void {
    this.dialogRef.close({
      newAddresses: event.newAddressList,
    });
  }

  async onSubmit() {
    if (this.createAddressForm.invalid) {
      return;
    }
    const formData = this.createAddressForm.value;
    let addressData: any = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phoneNo: formData.phoneNo.trim(),
      address1: formData.streetAddressLineOne.trim(),
      address2: formData.streetAddressLineTwo ? formData.streetAddressLineTwo.trim() : formData.streetAddressLineTwo,
      city: formData.town.trim(),
      country: formData.country ? formData.country.trim() : formData.country,
      postCode: formData.postCode.trim(),
      isDefault: true,
      addressType: formData.addressType,
      customerId: this.data.customerId,
      email: formData.email.trim().toLowerCase(),
    };

    if (!this.data.isEdit) {
      await this.userService.createUserAddress(addressData).subscribe({
        next: (response) => {
          this.dialogRef.close({
            status: true,
          });
        },
        error: (error) => {
          this.alertService.alert(error.error.message, 'error');
        },
      });
    } else {
      addressData.id = this.data.id;
      delete addressData.customerId;
      await this.userService.updateUserAddress(addressData, this.data.customerId).subscribe({
        next: (response) => {
          this.dialogRef.close({
            status: true,
          });
        },
        error: (error) => {
          this.alertService.alert(error.error.message, 'error');
        },
      });
    }
  }
}
